import { pipelineShort } from "./dealHealthColors";

/** Identity of one (pipeline, stage) rung. A JSON tuple rather than a joined
 *  string so a name containing the separator can't collide two rungs into one. */
export const stageKey = (pipeline: string, stage: string) =>
  JSON.stringify([pipeline, stage]);

export interface StageOption {
  value: string;
  label: string;
}

/** Minimal shape the stage filter needs from the Deal Health payload. */
interface StageSource {
  pipelines: string[];
  stages_by_pipeline: Record<string, string[]>;
  deals: { pipeline_name: string | null; stage_name: string | null }[];
}

/**
 * Options for the Deal Health stage multi-select, in FUNNEL order.
 *
 * Three rules, each there for a reason:
 *
 * 1. Ordered by the BE's stage ladders, not alphabetically — "Contract
 *    Negotiation" ahead of "Demo - Scheduled" reads as noise.
 * 2. Only stages with at least one open deal in the current pipeline scope.
 *    Offering an empty rung invites a filter that silently returns nothing.
 * 3. Filtering matches on stage NAME, and some names are shared across
 *    pipelines ("Exploration" is in both Customer Success and Partnerships).
 *    A shared name appears once, untagged, because selecting it genuinely spans
 *    both; tagging it with one pipeline would misreport what it selects. Names
 *    unique to one pipeline do get a tag, which is the informative case.
 *
 * @param pipeline currently selected pipeline, or null for all.
 */
export function stageOptionsFor(
  data: StageSource | undefined,
  pipeline: string | null,
): StageOption[] {
  const ladders = data?.stages_by_pipeline ?? {};
  const live = new Set(
    (data?.deals ?? [])
      .filter((d) => !pipeline || d.pipeline_name === pipeline)
      .map((d) => stageKey(d.pipeline_name ?? "", d.stage_name ?? "")),
  );
  const shown = (pipeline ? [pipeline] : data?.pipelines ?? []).map(
    (p) =>
      [p, (ladders[p] ?? []).filter((s) => live.has(stageKey(p, s)))] as const,
  );

  const pipelinesPerStage = new Map<string, number>();
  for (const [, stages] of shown) {
    for (const s of stages) {
      pipelinesPerStage.set(s, (pipelinesPerStage.get(s) ?? 0) + 1);
    }
  }

  const opts: StageOption[] = [];
  const seen = new Set<string>();
  for (const [p, stages] of shown) {
    for (const stage of stages) {
      if (seen.has(stage)) continue;
      seen.add(stage);
      const shared = (pipelinesPerStage.get(stage) ?? 1) > 1;
      opts.push({
        value: stage,
        label: pipeline || shared ? stage : `${stage} · ${pipelineShort(p)}`,
      });
    }
  }
  return opts;
}
